/**
 * MyAffiliates, `/postbacks/myaffiliates`, backed by `MyAffiliatesPostbackDto` +
 * `mapMyAffiliates`. This contract reproduces POSTBACK_LINKS_REFERENCE.md's
 * MyAffiliates section, which matches the DTO field-for-field.
 *
 * It deliberately does NOT reproduce the backoffice LinksPage `myaffiliates`
 * template. That template sends `subid={affiliate_id}&uid={player_id}&
 * tracking_tag={affiliate_id}` — none of `subid`, `uid`, `tracking_tag`,
 * `affiliate_id` or `player_id` exist on `MyAffiliatesPostbackDto` (its real
 * fields are `uid`, `payload`, `req_id`, `pub_id`, `media_id`, `campaign_id`,
 * ...). `{affiliate_id}` and `{player_id}` are also not in MyAffiliates' own
 * documented placeholder set (`{customer.id}`, `{payload}`, `{s2s.req_id}`,
 * `{s2s.pub_id}`, `{media.id}`, `{media.name}`, `{campaign.id}`,
 * `{campaign.name}`, `{brand.name}`). A link built from the backoffice
 * template would very likely arrive with the click identifier and campaign
 * data never sent at all. See the drift table — this is flagged as a
 * suspected functional bug, not a stylistic difference, and is left as an
 * open question for the project owner rather than silently "fixed" here.
 */
export const myaffiliatesContract = {
    id: 'myaffiliates',
    displayName: 'MyAffiliates',
    route: 'myaffiliates',
    trackingTagMacro: '{s2s.req_id}',
    amountBehavior: 'amount/currency are free-form; MyAffiliates does not document a fixed macro for either.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    pitfalls: [
        'MyAffiliates does not follow redirects (301/302) and does not retry failed postback requests (500/404).',
        'SUSPECTED BUG in the backoffice LinksPage template (not reproduced by this contract): it sends ' +
            'subid={affiliate_id}&uid={player_id}&tracking_tag={affiliate_id}, none of which are real ' +
            'MyAffiliatesPostbackDto fields or documented MyAffiliates placeholders. See drift table.',
    ],
    sourceRefs: [
        'apps/api/src/modules/postbacks/dto/myaffiliates-postback.dto.ts',
        'apps/api/src/modules/postbacks/services/parameter-mapper.service.ts:208-236 (mapMyAffiliates)',
        'POSTBACK_LINKS_REFERENCE.md:268-306',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'uid', macro: '{customer.id}' },
                { ourParam: 'payload', macro: '{payload}' },
                { ourParam: 'req_id', macro: '{s2s.req_id}' },
                { ourParam: 'pub_id', macro: '{s2s.pub_id}' },
                { ourParam: 'media_id', macro: '{media.id}' },
                { ourParam: 'media_name', macro: '{media.name}' },
                { ourParam: 'campaign_id', macro: '{campaign.id}' },
                { ourParam: 'campaign_name', macro: '{campaign.name}' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'uid', macro: '{customer.id}' },
                { ourParam: 'payload', macro: '{payload}' },
                { ourParam: 'req_id', macro: '{s2s.req_id}' },
                { ourParam: 'pub_id', macro: '{s2s.pub_id}' },
                { ourParam: 'media_id', macro: '{media.id}' },
                { ourParam: 'media_name', macro: '{media.name}' },
                { ourParam: 'campaign_id', macro: '{campaign.id}' },
                { ourParam: 'campaign_name', macro: '{campaign.name}' },
                { ourParam: 'amount', macro: 'AMOUNT' },
                { ourParam: 'currency', macro: 'CURRENCY' },
            ],
            notes: [
                'AMOUNT/CURRENCY are literal placeholders the operator must replace or remove — not MyAffiliates macros.',
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'qualified_cpa',
            params: [
                { ourParam: 'uid', macro: '{customer.id}' },
                { ourParam: 'payload', macro: '{payload}' },
                { ourParam: 'req_id', macro: '{s2s.req_id}' },
                { ourParam: 'pub_id', macro: '{s2s.pub_id}' },
                { ourParam: 'media_id', macro: '{media.id}' },
                { ourParam: 'media_name', macro: '{media.name}' },
                { ourParam: 'campaign_id', macro: '{campaign.id}' },
                { ourParam: 'campaign_name', macro: '{campaign.name}' },
                { ourParam: 'amount', macro: 'AMOUNT' },
                { ourParam: 'currency', macro: 'CURRENCY' },
            ],
            notes: [
                'AMOUNT/CURRENCY are literal placeholders the operator must replace or remove — not MyAffiliates macros.',
            ],
        },
    ],
};
/**
 * ⚠️ SUSPECTED BUG, reproduced byte-for-byte, not corrected. This is
 * backoffice's CURRENT `myaffiliates` output (`test/fixtures.ts`:
 * `backoffice.myaffiliates`): `subid={affiliate_id}&uid={player_id}&
 * tracking_tag={affiliate_id}`. None of `subid`, `uid`, `tracking_tag`,
 * `affiliate_id`, `player_id` are fields on `MyAffiliatesPostbackDto`
 * (real fields: `uid`, `payload`, `req_id`, `pub_id`, `media_id`,
 * `campaign_id`, ...), and `{affiliate_id}`/`{player_id}` are not in
 * MyAffiliates' own documented placeholder set either (see
 * `myaffiliatesContract` above for that set). A brand actually configured
 * with this template is very likely sending its click identifier and
 * campaign data nowhere — see drift table row 6,
 * docs/solutions/postback-contract-registry.md, flagged as the highest-
 * priority open question for the project owner.
 *
 * This contract exists ONLY so a migration of the backoffice surface can
 * reproduce its own current (probably broken) output exactly, without
 * silently "fixing" it to `myaffiliatesContract` as a side effect of
 * routing through this package. TMA and the Telegram bot expose no
 * MyAffiliates entry at all, so no equivalent contract is needed for them.
 */
export const myaffiliatesBackofficeContract = {
    id: 'myaffiliates-backoffice',
    displayName: 'MyAffiliates (backoffice current output — suspected non-functional)',
    route: 'myaffiliates',
    trackingTagMacro: '{affiliate_id}',
    amountBehavior: 'FTD/CPA amount from {amount}, currency from {currency} — but see the pitfall: {affiliate_id} and ' +
        '{player_id} are not real MyAffiliates macros, so this template may not carry any real data at all.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    pitfalls: [
        '⚠️ SUSPECTED BUG: subid={affiliate_id}, uid={player_id} and tracking_tag={affiliate_id} do not ' +
            'correspond to any MyAffiliatesPostbackDto field or documented MyAffiliates placeholder. Do not ' +
            'issue this template to a new brand before the open question in the drift table is resolved.',
    ],
    sourceRefs: [
        'apps/backoffice/src/pages/LinksPage.tsx (TEMPLATES.myaffiliates)',
        'apps/api/src/modules/postbacks/dto/myaffiliates-postback.dto.ts (no affiliate_id/player_id/subid/uid/tracking_tag fields)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'subid', macro: '{affiliate_id}' },
                { ourParam: 'uid', macro: '{player_id}' },
                { ourParam: 'tracking_tag', macro: '{affiliate_id}' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'subid', macro: '{affiliate_id}' },
                { ourParam: 'uid', macro: '{player_id}' },
                { ourParam: 'amount', macro: '{amount}' },
                { ourParam: 'currency', macro: '{currency}' },
                { ourParam: 'tracking_tag', macro: '{affiliate_id}' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'subid', macro: '{affiliate_id}' },
                { ourParam: 'uid', macro: '{player_id}' },
                { ourParam: 'amount', macro: '{amount}' },
                { ourParam: 'currency', macro: '{currency}' },
                { ourParam: 'tracking_tag', macro: '{affiliate_id}' },
            ],
        },
    ],
};
//# sourceMappingURL=myaffiliates.js.map